import { Component, Vue } from 'vue-property-decorator';
import Sidepanel from '@/views/Webshop/Sidepanel/Sidepanel';
import Draggable from 'vuedraggable';
import { Set } from '@/models/Set';
import { OrderItem } from '@/models/OrderItem';
import { SetItem } from '@/models/SetItem';
import { AxiosError } from 'axios';
import debounce from 'debounce';
import ErrorHandler from '@/support/ErrorHandler';
import { currency } from '@/support/String';
import { Order } from '@/models/Order';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { Color } from '@/models/Color';
import { SubColor } from '@/models/SubColor';
import { Options } from '@/components/mi-dialog/MiDialog';
import { Contact as ContactModel } from '@/models/Contact';
import { BoxRequest } from '@/models/BoxRequest';

@Component<OrderSets>({
  components: {
    Sidepanel,
    Draggable,
    LoaderCard,
  },
  filters: {
    currency(value: string) {
      return !value ? '' : currency(parseInt(value));
    },
  },
})
export default class OrderSets extends Vue {
  public $pageTitle = 'Sets';
  protected currentOrderId: string = '';
  protected orderItems: SetProduct[] = [];
  protected orderItemsIds: string[] = [];
  protected setProductIds: string[] = [];
  protected pack: boolean = false;
  protected totalPrice: number = 0;
  protected totalItems: number = 0;
  public nameDebounce: Function = this.setNameChange();
  public textDebounce: Function = this.setTextChange();
  protected dropAllowed: boolean = true;
  protected isLoading: boolean = false;
  protected recommendations: string[] = [];

  protected sets: OrderSet[] = [];
  protected set: SetProduct[] = [];

  protected orderType: string = '';
  protected order: Order | null = null;
  protected orderStatus: string = '';
  protected orderUserId: string = '';
  protected latestBoxRequest: BoxRequest | null = null;

  protected contact: ContactModel = new ContactModel();
  protected contactAnswers: any[] = [];
  protected contactImages: any[] = [];

  protected selectedOrderItemId: string | null = null;
  protected selectedProductID: string | null = null;

  protected hasMaxSets: boolean = this.sets.length === 3;

  public sayColor(setProduct: SetProduct): string {
    const productColor = setProduct.color as Color;

    if (!productColor || !productColor.sub_colors) {
      return '';
    }

    const subColors = productColor.sub_colors as SubColor[];
    return subColors.length
      ? subColors
        .map((subColor: SubColor) => {
          return subColor.name;
        })
        .join(', ')
      : productColor.name || '';
  }

  public mounted() {
    this.currentOrderId = this.$route.params.id;
    this.initialize();
  }

  protected initialize() {
    this.isLoading = true;
    this.listOrderItems();
    this.getOrder();
    this.getRecommendation();
  }

  protected getOrder() {
    new Order()
      .include(['user'])
      .find(this.currentOrderId)
      .then((order: any) => {
        if (!order) {
          return;
        }

        this.order = order;
        this.orderType = order.box_type;
        this.orderStatus = order.status as string;
        this.orderUserId = order.user ? (order.user.id as string) : '';

        if (!this.orderUserId) {
          return;
        }

        this.getContact();
        this.getBoxRequest();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async getBoxRequest() {
    if (!this.orderUserId) {
      return;
    }

    try {
      const requestBoxes = await new BoxRequest()
        .filter({ user: this.orderUserId })
        .sort('created_at', 'DESC')
        .include(['info'])
        .all();
      const styledTypeBoxRequests = requestBoxes.filter((requestBox: BoxRequest) => requestBox.type === 'styled_box');
      if (styledTypeBoxRequests.length) {
        this.latestBoxRequest = styledTypeBoxRequests[0];
      }
    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  protected getContact() {
    new ContactModel()
      .include(['profile', 'answers'])
      .find(this.orderUserId)
      .then((contact: ContactModel) => {
        this.contact = contact;
        this.$root.$emit('breadcrumbUpdated', {
          crumb: [{ name: 'Orders', path: `/orders` }, { name: `${this.contact.name}` }],
        });

        if (!this.contact.profile || !this.contact.profile.answers) {
          return;
        }

        this.contact.profile.answers = JSON.parse((this.contact.profile as any).answers);

        this.contact.answers = [...(this.contact.profile as any).answers];
        this.contactAnswers = [...(this.contact.profile as any).answers];
        this.$store.dispatch('updateLeadAnswers', this.contact);
      });
  }

  protected getRecommendation() {
    new Order().getRecommendation(this.currentOrderId).then((result: any) => {
      if (result.messages) {
        this.recommendations = result.messages;
      }
    });
  }

  protected countItems(orderItemId: string) {
    let count = 0;
    this.setProductIds.forEach((id: string) => {
      if (orderItemId === id) {
        count++;
      }
    });
    return count;
  }

  protected remove(index: number) {
    this.set.splice(index, 1);
  }

  protected listOrderItems() {
    new OrderItem()
      .order(this.currentOrderId)
      .include(['product', 'color', 'supplier', 'size', 'brand', 'season'])
      .limit(100)
      .all()
      .then((response: any) => {
        this.orderItems = response;
        this.orderItems.forEach((orderItem: SetProduct) => {
          this.orderItemsIds.push(orderItem.id);
        });

        this.listSets();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected listSets() {
    new Set()
      .order(this.currentOrderId)
      .include(['set-items', 'product', 'color', 'size', 'supplier', 'brand'])
      .all()
      .then((response: any) => {
        this.sets = response;
        this.setProductIds = [];
        this.sets.forEach((set: OrderSet) => {
          if (set.set_items) {
            set.set_items.forEach((setProduct: SetProduct) => {
              this.setProductIds.push((setProduct as any).product.id);
            });
          }
        });

        this.hasMaxSets = this.sets.length === 3;

        this.calculateTotal();
        this.calculateTotalItems();
        if (this.sets.length === 0) {
          this.createNewSet(this.$tc('orders.orderDetail.webshop.sets.otherItems'));
        }
        this.isLoading = false;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected checkForDuplicates(targetList: object[], draggedElement: SetProduct) {
    let duplicate = false;
    for (let i = 0; i < targetList.length; i += 1) {
      if ((targetList[i] as any).product.id === (draggedElement as any).product.id) {
        duplicate = true;
      }
    }

    return duplicate;
  }

  protected addAllToSet() {
    this.orderItems.forEach((orderItem: SetProduct) => {
      this.createSetItem(this.sets[0].id, orderItem as any);
    });
  }

  protected get canAddToSet() {
    if (this.sets.length > 1 || (this.sets[0] && this.sets[0].set_items.length > 0)) {
      return false;
    }
    return true;
  }

  protected onMove(event: any) {
    let targetList = event.relatedContext.list;
    let draggedElement = event.draggedContext.element;
    if (this.checkForDuplicates(targetList, draggedElement)) {
      this.dropAllowed = false;
      return false;
    }

    this.dropAllowed = true;
  }

  protected onEnd(event: any) {
    const setId = event.to.id;
    const orderItemId = event.item.id;

    const orderItem = this.orderItems.find((orderItem: any) => {
      return orderItemId === orderItem.id;
    });

    if (this.dropAllowed === false) {
      return;
    }

    this.createSetItem(setId, orderItem as any);
  }

  protected setNameChange() {
    return debounce((setId: string, name: KeyboardEvent, text: string) => {
      this.updateSet((name as any).target.value, setId, text);
    }, 1000);
  }

  protected setTextChange() {
    return debounce((setId: string, text: string) => {
      this.updateSetText(setId, text);
    }, 1000);
  }

  protected createSetItem(setId: string, orderItem: OrderItem) {
    const packInSet = orderItem.product && orderItem.product.id && this.countItems(orderItem.product.id) < 1;

    const setItemPayload: Partial<SetItem> = {
      order_item: orderItem.id,
      setId: setId,
      pack_with_this_set: packInSet,
    };
    new SetItem()
      .order(this.currentOrderId)
      .set(setId)
      .create(setItemPayload)
      .then((response: any) => {
        this.listSets();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected updateSet(name: string, setId: string, text: string) {
    const setPayload: Partial<Set> = {
      name: name,
      text: text,
    };
    new Set()
      .orderSet(this.currentOrderId, setId)
      .update(setPayload)
      .then((response: any) => {
        this.listSets();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected updateSetText(setId: string, text: string) {
    const setPayload: Partial<Set> = {
      text: text,
    };
    new Set()
      .orderSet(this.currentOrderId, setId)
      .update(setPayload)
      .then((response: any) => {
        //
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected deleteSet(setId: string) {
    new Set()
      .deleteOrderSet(this.currentOrderId, setId)
      .then((response: any) => {
        this.listSets();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected updateSetItem(pack: boolean, setId: string, setItemId: string) {
    const setItemPayload: Partial<SetItem> = {
      pack_with_this_set: pack,
    };
    new SetItem()
      .updateSetItem(this.currentOrderId, setId, setItemId, setItemPayload)
      .then((response: any) => {
        this.listSets();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected deleteSetItem(setItemId: string, setId: string) {
    new SetItem()
      .deleteSetItem(this.currentOrderId, setId, setItemId)
      .then((response: any) => {
        this.listSets();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected createNewSet(setName: string) {
    const setPayload: Partial<Set> = {
      name: setName,
    };
    new Set()
      .order(this.currentOrderId)
      .create(setPayload)
      .then((response: any) => {
        this.listSets();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected deleteCartItem(id: string) {
    new OrderItem()
      .deleteOrderItem(this.$route.params.id, id)
      .then((response: any) => {
        this.$emit('orderItemsUpdated');
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected confirmDelete(productID: string, orderItemID: string) {
    this.selectedProductID = productID;
    this.selectedOrderItemId = orderItemID;

    this.$store.dispatch('openDialog', this.dialogDeleteFromOrderOptions);
  }

  get dialogDeleteFromOrderOptions(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Weet je zeker dat je dit item wilt verwijderen uit de order?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Verwijderen',
          action: () => {
            if (!this.selectedProductID || !this.selectedOrderItemId) {
              return;
            }

            this.deleteFromAll(this.selectedProductID, this.selectedOrderItemId);
          },
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
          action: () => {
            this.selectedOrderItemId = null;
            this.selectedProductID = null;
          },
        },
      },
    };
  }

  protected deleteFromAll(productID: string, orderItemID: string) {
    const setsMetItem = this.sets.filter((set: OrderSet) => {
      return set.set_items.find((setItem: SetProduct) => {
        return setItem.product.id === productID;
      });
    });

    const deleteSetItemPromises = setsMetItem.forEach((set: OrderSet) => {
      const setID = set.id;
      set.set_items.forEach((setItem: SetProduct) => {
        if (setItem.product.id === productID) {
          this.deleteSetItem(setItem.id, setID);
        }
      });
    });

    const deleteCartItemPromise = this.deleteCartItem(orderItemID);

    Promise.all([deleteSetItemPromises, deleteCartItemPromise])
      .then((response: any) => {
        const itemInCart = this.orderItems.find(
          (orderItem: SetProduct) => orderItem.product.id === productID,
        ) as SetProduct;
        const cartIndex = this.orderItems.indexOf(itemInCart);

        if (cartIndex !== -1) {
          this.orderItems.splice(cartIndex, 1);
        }
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected calculateTotal() {
    let calculatedItems: string[] = [];
    this.totalPrice = 0;
    this.sets.forEach((set: OrderSet) => {
      if (set.set_items) {
        set.set_items.forEach((setProduct: SetProduct) => {
          if (calculatedItems.indexOf((setProduct as any).product.id) === -1) {
            this.totalPrice += parseInt((setProduct as any).product.retail_price);
          }
          calculatedItems.push((setProduct as any).product.id);
        });
      }
    });
    return this.totalPrice;
  }

  protected calculateTotalItems() {
    let calculatedItems: string[] = [];
    this.totalItems = 0;
    this.sets.forEach((set: OrderSet) => {
      if (set.set_items) {
        set.set_items.forEach((setProduct: SetProduct) => {
          if (calculatedItems.indexOf((setProduct as any).product.id) === -1) {
            this.totalItems += 1;
          }
          calculatedItems.push((setProduct as any).product.id);
        });
      }
    });
    return this.totalItems;
  }

  protected toOverview() {
    this.sets.forEach((set: any) => {
      set
        .updateOrderSet(this.currentOrderId, set.id, { text: set.text })
        .then(() => {
          this.$router.push({
            name: 'webshop.order.overview',
          });
        })
        .catch((error: AxiosError) => {
          ErrorHandler.network(error);
        });
    });
  }

  protected packWithSet(event: any, setId: string, item: any) {
    this.updateSetItem(event, setId, item.id);

    const productId = item.product.id;
    this.sets.forEach((set: OrderSet) => {
      if (set.id !== setId && set.set_items) {
        set.set_items.forEach((setItem: any) => {
          if (setItem.product.id === productId) {
            setItem.pack_with_this_set = false;
            this.updateSetItem(false, set.id, setItem.id);
          }
        });
      }
    });
  }

  protected updateProductQuantity(orderItem: OrderItem) {
    if (!this.order) {
      return;
    }

    const payload = {
      quantity: orderItem.quantity,
    };

    orderItem
      .updateOrderItem(this.order.id as string, orderItem.id as string, payload)
      .then(() => {
        //
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  //#region Getters & Setters
  /**
   * ie.
   * protected get companyName(): string {
   *  return this?.company.name || 'N/A';
   * }
   */
  //#endregion
  protected get hasMaxProducts(): boolean {
    if (this.orderType === 'supplier_return') {
      return false;
    }

    if (this.order && this.order.user_name === 'Foto Studio') {
      return false;
    }

    return this.totalItems > 19;
  }
}

interface SetProduct {
  name: string;
  brand: number;
  color: string | Color;
  id: string;
  image_url: string;
  product: Product;
  retail_price: string;
  size: string;
  supplier: string;
}

interface OrderSet {
  id: string;
  name: string;
  set_items: SetProduct[];
}

interface Product {
  id: string;
}
